/**
 * Cadastro e edição da frente de obra (#365, D10): uma tela só, com três campos — Obra, Nome e
 * Responsável — e "Salvar e cadastrar outra" para quem cadastra várias frentes da mesma obra.
 * Etapas, riscos e trabalhadores se preenchem na ficha, depois de salvar.
 *
 * Alvo visual: docs/superpowers/specs/2026-10-16-365-frentes-obra-mockup.html (tela 2).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Alert, Box, Button, Card, CardContent, Skeleton, TextField, Typography } from '@mui/material';

import { PageShell, RouteActionButton } from '../../components';
import { PrerequisiteBlock } from '../../components/common/PrerequisiteBlock';
import { pode } from '../../authorization/pode';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { workFrontService } from '../../services/workFrontService';
import { ApiRequestError } from '../../types/common';
import type { WorkFrontListItem } from '../../types/workFront';
import { HELPER_TEXT_PROPS, LABEL_ABOVE } from '../fire-equipments/components/formFieldProps';
import {
  EMPTY_VALUES,
  buildCreatePayload,
  buildUpdatePayload,
  validateField,
  validateForm,
  valuesForNext,
  valuesFromFront,
  type WorkFrontFormErrors,
  type WorkFrontFormField,
  type WorkFrontFormValues,
} from './workFrontForm';

const TOUCH_TARGET = 44;
/**
 * Início da mensagem do servidor para nome repetido na mesma obra. Ele chega como um
 * `BUSINESS_RULE_ERROR` genérico, sem campo estruturado que o distinga de outras regras de
 * negócio; enquanto isso não existir, só o texto o reconhece.
 */
const NAME_TAKEN_PREFIX = 'Já existe uma frente';

const errorMessage = (err: unknown, fallback: string): string => (err instanceof Error ? err.message : fallback);

const isNotFound = (err: unknown): boolean => err instanceof ApiRequestError && err.status === 404;

type Option = { value: string; label: string };

export const WorkFrontFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const [front, setFront] = useState<WorkFrontListItem | null>(null);
  const [values, setValues] = useState<WorkFrontFormValues>({
    ...EMPTY_VALUES,
    unitId: (searchParams.get('unitId') ?? '').replace(/\D/g, ''),
  });
  const [errors, setErrors] = useState<WorkFrontFormErrors>({});
  const [loaded, setLoaded] = useState(!editing);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [focusToken, setFocusToken] = useState(0);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    setLoaded(false);
    setLoadError(null);
    setNotFound(false);
    workFrontService
      .getById(id)
      .then((response) => {
        if (!alive) return;
        setFront(response);
        setValues(valuesFromFront(response));
        setLoaded(true);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (isNotFound(err)) setNotFound(true);
        else setLoadError(errorMessage(err, 'Erro ao carregar a frente'));
      });
    return () => {
      alive = false;
    };
  }, [id, attempt]);

  useEffect(() => {
    if (focusToken > 0) nameRef.current?.focus();
  }, [focusToken]);

  const loadObraOptions = useCallback(async (): Promise<readonly Option[]> => {
    const obras = await workFrontService.obras();
    return obras.map((obra) => ({ value: String(obra.id), label: obra.name }));
  }, []);
  const obraList = useListaPermitida('work_front.read', 'Obras', loadObraOptions);

  const unitId = values.unitId;
  const loadEmployeeOptions = useCallback(async (): Promise<readonly Option[]> => {
    const employees = await workFrontService.eligibleEmployees(unitId);
    return employees.map((employee) => ({
      value: String(employee.id),
      label: employee.occupation ? `${employee.name} · ${employee.occupation}` : employee.name,
    }));
  }, [unitId]);
  const employeeList = useListaPermitida('work_front.read', 'Funcionários', loadEmployeeOptions, {
    habilitado: loaded && unitId !== '',
  });

  // Obra única e ainda não escolhida: a escolha é óbvia, então vem feita.
  useEffect(() => {
    if (editing || !obraList.resolvida || obraList.itens.length === 0) return;
    setValues((current) => {
      if (current.unitId !== '' && obraList.itens.some((obra) => obra.value === current.unitId)) return current;
      return { ...current, unitId: obraList.itens.length === 1 ? obraList.itens[0].value : '' };
    });
  }, [editing, obraList.resolvida, obraList.itens]);

  const obraOptions = useMemo<readonly Option[]>(() => {
    if (!front || obraList.itens.some((obra) => obra.value === String(front.unit.id))) return obraList.itens;
    return [{ value: String(front.unit.id), label: front.unit.name }, ...obraList.itens];
  }, [front, obraList.itens]);

  const employeeOptions = useMemo<readonly Option[]>(() => {
    const responsible = front?.responsible;
    if (!responsible || employeeList.itens.some((employee) => employee.value === String(responsible.id))) {
      return employeeList.itens;
    }
    return [{ value: String(responsible.id), label: responsible.name }, ...employeeList.itens];
  }, [front, employeeList.itens]);

  const options = { requireUnit: !editing };
  const noObra = !editing && obraList.permitido && obraList.resolvida && !obraList.erro && obraList.itens.length === 0;
  const obrasLoading = obraList.permitido && !obraList.resolvida;
  const employeesLoading = employeeList.permitido && !employeeList.resolvida && unitId !== '';

  const change = (field: WorkFrontFormField, value: string) => {
    const next = { ...values, [field]: value };
    if (field === 'unitId') next.responsibleEmployeeId = '';
    setValues(next);
    if (errors[field]) {
      setErrors((current) => ({ ...current, [field]: validateField(field, next, options) ?? undefined }));
    }
  };

  const blur = (field: WorkFrontFormField) => {
    setErrors((current) => ({ ...current, [field]: validateField(field, values, options) ?? undefined }));
  };

  const save = async (another: boolean) => {
    const found = validateForm(values, options);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setFormError('Corrija os campos marcados para salvar.');
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      if (editing && id) {
        await workFrontService.update(id, buildUpdatePayload(values));
        enqueueSnackbar(`${values.name.trim()} atualizada.`, { variant: 'success' });
        navigate(`/work-fronts/${id}`);
        return;
      }

      const created = await workFrontService.create(buildCreatePayload(values));
      enqueueSnackbar(`${created.name} cadastrada.`, { variant: 'success' });
      if (another) {
        setValues(valuesForNext(values));
        setErrors({});
        setFocusToken((token) => token + 1);
      } else {
        navigate(`/work-fronts/${created.id}`);
      }
    } catch (err: unknown) {
      const message = errorMessage(err, 'Não foi possível salvar a frente.');
      if (err instanceof ApiRequestError && err.code === 'BUSINESS_RULE_ERROR' && message.startsWith(NAME_TAKEN_PREFIX)) {
        setErrors((current) => ({ ...current, name: message }));
        setFocusToken((token) => token + 1);
      } else {
        setFormError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title = editing ? 'Editar frente de obra' : 'Nova frente de obra';
  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Frentes de obra', path: '/work-fronts' },
    { label: editing ? 'Editar' : 'Nova' },
  ];

  if (notFound) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={title}>
        <Alert
          severity="info"
          action={
            <RouteActionButton
              para="/work-fronts"
              exige={pode('work_front.read')}
              color="inherit"
              sx={{ minHeight: TOUCH_TARGET }}
            >
              Voltar para a lista
            </RouteActionButton>
          }
        >
          Não encontramos esta frente. Ela pode ter sido excluída ou ser de outra empresa.
        </Alert>
      </PageShell>
    );
  }

  if (loadError) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={title}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => setAttempt((count) => count + 1)} sx={{ minHeight: TOUCH_TARGET }}>
              Tentar de novo
            </Button>
          }
        >
          {loadError}
        </Alert>
      </PageShell>
    );
  }

  if (!loaded) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={title}>
        <Skeleton variant="rounded" height={260} />
      </PageShell>
    );
  }

  const backTo = editing && id ? `/work-fronts/${id}` : '/work-fronts';
  const fieldBase = (field: WorkFrontFormField, label: string, required = false) => ({
    label,
    required,
    value: values[field],
    error: Boolean(errors[field]),
    InputLabelProps: LABEL_ABOVE,
    FormHelperTextProps: HELPER_TEXT_PROPS,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => change(field, event.target.value),
    onBlur: () => blur(field),
  });
  const nativeSelect = { select: true, SelectProps: { native: true } } as const;

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title={title}
      subtitle={editing && front ? `${front.name} · ${front.unit.name}` : undefined}
    >
      {formError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setFormError(null)}>
          {formError}
        </Alert>
      )}

      {obraList.erro && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" onClick={obraList.recarregar} sx={{ minHeight: TOUCH_TARGET }}>
              Tentar de novo
            </Button>
          }
        >
          {obraList.erro}
        </Alert>
      )}

      {noObra && (
        <Box sx={{ mb: 3 }}>
          <PrerequisiteBlock
            title="Nenhuma unidade está marcada como obra"
            reason="As frentes ficam dentro de uma obra, e a obra é uma unidade da empresa. Marque a unidade onde o trabalho acontece e volte aqui."
            resolve={{ label: 'Ir para Unidades', permission: 'unit.update', mode: 'navigate', route: '/units' }}
            deniedHint="Peça a quem administra a empresa para marcar uma unidade como obra."
          />
        </Box>
      )}

      <Box
        component="form"
        noValidate
        onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          void save(false);
        }}
      >
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
              Qual é a frente
            </Typography>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <TextField
                {...fieldBase('unitId', 'Obra', true)}
                {...nativeSelect}
                disabled={editing || !obraList.permitido || obrasLoading || noObra}
                helperText={
                  editing
                    ? 'A frente não muda de obra. Para mudar, crie outra frente.'
                    : (errors.unitId ??
                      obraList.bloqueio ??
                      (obrasLoading ? 'Carregando obras…' : 'Só aparecem as unidades marcadas como obra.'))
                }
              >
                <option value="">Selecione…</option>
                {obraOptions.map((obra) => (
                  <option key={obra.value} value={obra.value}>
                    {obra.label}
                  </option>
                ))}
              </TextField>
              <TextField
                {...fieldBase('name', 'Nome da frente', true)}
                inputRef={nameRef}
                placeholder="Ex.: Estrutura, torre A"
                inputProps={{ maxLength: 120 }}
                helperText={errors.name ?? 'Não repete dentro da mesma obra.'}
              />
              <TextField
                {...fieldBase('responsibleEmployeeId', 'Responsável pela frente')}
                {...nativeSelect}
                disabled={unitId === '' || !employeeList.permitido || employeesLoading}
                helperText={
                  employeeList.bloqueio ??
                  employeeList.erro ??
                  (unitId === '' ? 'Escolha a obra primeiro.' : undefined) ??
                  (employeesLoading ? 'Carregando funcionários…' : undefined) ??
                  'Funcionário da obra. Sem responsável, a emissão do documento avisa.'
                }
              >
                <option value="">Nenhum</option>
                {employeeOptions.map((employee) => (
                  <option key={employee.value} value={employee.value}>
                    {employee.label}
                  </option>
                ))}
              </TextField>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <Button type="submit" variant="contained" disabled={submitting || noObra} sx={{ minHeight: TOUCH_TARGET }}>
            Salvar frente
          </Button>
          {!editing && (
            <Button
              variant="outlined"
              disabled={submitting || noObra}
              onClick={() => void save(true)}
              sx={{ minHeight: TOUCH_TARGET }}
            >
              Salvar e cadastrar outra
            </Button>
          )}
          <RouteActionButton
            para={backTo}
            exige={pode('work_front.read')}
            disabled={submitting}
            sx={{ minHeight: TOUCH_TARGET }}
          >
            Cancelar
          </RouteActionButton>
        </Box>
        {!editing && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Salvar guarda a frente e abre a ficha dela, para cadastrar as etapas e os prazos. &quot;Salvar e cadastrar
            outra&quot; mantém a obra e limpa o nome e o responsável.
          </Typography>
        )}
      </Box>
    </PageShell>
  );
};

export default WorkFrontFormPage;
